import React, { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, Users, Crown, AlertTriangle, UserPlus, UserX } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ai } from "@/lib/api";
import { formatIDR, formatNumber } from "@/lib/format";

const segmentMeta = {
  champions: { label: "Champions", icon: Crown, color: "purple", desc: "Pelanggan terbaik — beli sering, spending tinggi, baru saja order. Prioritaskan retensi & referral." },
  loyal: { label: "Loyal", icon: Users, color: "emerald", desc: "Repeat buyer yang konsisten. Tawarkan membership upgrade & bundling premium." },
  at_risk: { label: "At Risk", icon: AlertTriangle, color: "amber", desc: "Dulu aktif, sekarang jarang order. Winback dengan cashback agresif." },
  new: { label: "New", icon: UserPlus, color: "blue", desc: "Baru order 1x. Kirim onboarding + reminder reorder untuk convert jadi loyal." },
  lost: { label: "Lost", icon: UserX, color: "gray", desc: "Sudah lama tidak order. Coba reactivation campaign atau segmen ini bisa di-skip." },
};

export default function AISegmentation() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await ai.segmentation();
      setData(res);
      toast.success("Segmentasi berhasil dibuat");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal");
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="inline-flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase text-purple-600 bg-purple-50 border border-purple-200 rounded-full px-2.5 py-1">
          <Sparkles size={12} /> RFM Clustering + Claude AI
        </div>
        <h1 className="mt-3 font-heading text-2xl sm:text-3xl font-bold text-gray-900">AI Customer Segmentation</h1>
        <p className="text-sm text-gray-500 mt-1">Bagi pelanggan Anda ke dalam 5 segmen berdasarkan RFM (Recency, Frequency, Monetary). Dapat strategi per segmen dari AI.</p>
      </div>

      {!data && (
        <Card className="border-gray-200 border-dashed">
          <CardContent className="p-12 text-center">
            <Sparkles className="mx-auto text-gray-300" size={56} />
            <h3 className="mt-4 font-heading text-lg font-semibold text-gray-900">Belum ada analisis</h3>
            <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">Klik tombol di bawah untuk analisa segmen customer Anda. AI akan otomatis buatkan strategi per segmen.</p>
            <Button onClick={run} disabled={loading} className="mt-6 bg-purple-600 hover:bg-purple-700 text-white h-11 px-6" data-testid="run-segmentation">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              Jalankan Segmentasi
            </Button>
          </CardContent>
        </Card>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(data.segments || {}).map(([key, seg]) => {
              const meta = segmentMeta[key];
              const Icon = meta.icon;
              return (
                <Card key={key} className={`border-${meta.color}-200 bg-${meta.color}-50/40`}>
                  <CardContent className="p-4">
                    <div className={`w-8 h-8 rounded-lg bg-${meta.color}-100 text-${meta.color}-700 flex items-center justify-center mb-2`}><Icon size={16} /></div>
                    <div className="text-[10px] font-bold tracking-wider uppercase text-gray-500">{meta.label}</div>
                    <div className="mt-1 font-heading text-3xl font-bold text-gray-900">{formatNumber(seg.count)}</div>
                    <div className="text-xs text-gray-500">pelanggan</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {data.narrative && (
            <Card className="border-purple-200 bg-purple-50/30">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-purple-700">
                  <Sparkles size={12} /> Strategi AI per Segmen
                </div>
                <div className="mt-3 whitespace-pre-wrap text-sm text-gray-800 leading-relaxed">{data.narrative}</div>
              </CardContent>
            </Card>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            {Object.entries(data.segments || {}).map(([key, seg]) => {
              const meta = segmentMeta[key];
              if (seg.count === 0) return null;
              return (
                <Card key={key} className="border-gray-200">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2">
                      <div className={`w-9 h-9 rounded-lg bg-${meta.color}-100 text-${meta.color}-700 flex items-center justify-center`}><meta.icon size={16} /></div>
                      <div>
                        <div className="font-heading font-semibold text-gray-900">{meta.label} ({seg.count})</div>
                        <div className="text-xs text-gray-500">{meta.desc}</div>
                      </div>
                    </div>
                    {seg.sample_customers?.length > 0 && (
                      <div className="mt-3 border-t border-gray-100 pt-3">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Sample</div>
                        <div className="space-y-1">
                          {seg.sample_customers.map((c) => (
                            <div key={c.customer_id || `${c.name}-${c.total_order}`} className="flex items-center justify-between text-xs">
                              <div><b className="text-gray-900">{c.name}</b> · {c.total_order} order</div>
                              <div className="text-emerald-600 font-semibold">{formatIDR(c.total_spending)}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Button onClick={run} disabled={loading} variant="outline" data-testid="rerun-segmentation">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
            Jalankan Ulang
          </Button>
        </>
      )}
    </div>
  );
}
